/**
 * Robust data persistence utilities to prevent data loss
 */

/**
 * Safe localStorage operations with error handling
 */
export const safeLocalStorage = {
  /**
   * Safely get item from localStorage
   * @param {string} key - Storage key
   * @returns {any} - Parsed data or null
   */
  getItem: (key) => {
    try {
      // Check if localStorage is available (not available during SSR)
      if (typeof window === 'undefined' || !window.localStorage) {
        return null;
      }
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('Error reading from localStorage');
      return null;
    }
  },

  /**
   * Safely set item in localStorage
   * @param {string} key - Storage key
   * @param {any} value - Data to store
   * @returns {boolean} - Success status
   */
  setItem: (key, value) => {
    try {
      // Check if localStorage is available (not available during SSR)
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error('Error writing to localStorage');
      return false;
    }
  },

  /**
   * Safely remove item from localStorage
   * @param {string} key - Storage key
   * @returns {boolean} - Success status
   */
  removeItem: (key) => {
    try {
      // Check if localStorage is available (not available during SSR)
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing from localStorage');
      return false;
    }
  }
};

/**
 * Form data persistence with backup and recovery
 */
export class FormDataManager {
  constructor(storageKey, submittedKey) {
    this.storageKey = storageKey;
    this.submittedKey = submittedKey;
    this.backupKey = `${storageKey}_backup`;
    this.lockKey = `${storageKey}_lock`;
  }

  /**
   * Load form data with fallback to backup
   * @returns {Object} - Form data
   */
  loadData() {
    // Check if form is locked (being submitted)
    const isLocked = safeLocalStorage.getItem(this.lockKey);
    if (isLocked && Date.now() - isLocked < 30000) { // 30 second lock
      // Form is currently being submitted, using backup data
      return safeLocalStorage.getItem(this.backupKey) || {};
    }

    // Try to load main data
    const data = safeLocalStorage.getItem(this.storageKey);
    if (data) {
      return data;
    }

    // Fallback to backup
    const backupData = safeLocalStorage.getItem(this.backupKey);
    if (backupData) {
      // Using backup data due to missing main data
      return backupData;
    }

    return {};
  }

  /**
   * Save form data with backup
   * @param {Object} data - Form data to save
   * @returns {boolean} - Success status
   */
  saveData(data) {
    const success = safeLocalStorage.setItem(this.storageKey, data);
    if (success) {
      // Create backup
      safeLocalStorage.setItem(this.backupKey, data);
    }
    return success;
  }

  /**
   * Lock form during submission
   * @returns {boolean} - Success status
   */
  lockForm() {
    return safeLocalStorage.setItem(this.lockKey, Date.now());
  }

  /**
   * Unlock form after submission
   * @returns {boolean} - Success status
   */
  unlockForm() {
    return safeLocalStorage.removeItem(this.lockKey);
  }

  /**
   * Mark form as submitted
   * @param {boolean} keepBackup - Whether to keep backup data for recovery (default: true)
   * @returns {boolean} - Success status
   */
  markSubmitted(keepBackup = true) {
    const success = safeLocalStorage.setItem(this.submittedKey, true);
    if (success) {
      // Clear main data and lock
      safeLocalStorage.removeItem(this.storageKey);
      safeLocalStorage.removeItem(this.lockKey);
      
      // Optionally remove backup data if not needed for recovery
      if (!keepBackup) {
        safeLocalStorage.removeItem(this.backupKey);
        safeLocalStorage.removeItem(this.submittedKey);
      }
    }
    return success;
  }

  /**
   * Check if form was already submitted
   * @returns {boolean} - Submitted status
   */
  isSubmitted() {
    return safeLocalStorage.getItem(this.submittedKey) === true;
  }

  /**
   * Clear all form data (for testing/reset)
   * @returns {boolean} - Success status
   */
  clearAll() {
    const keys = [this.storageKey, this.backupKey, this.lockKey, this.submittedKey];
    return keys.every(key => safeLocalStorage.removeItem(key));
  }

  /**
   * Recover data from backup if main data is lost
   * @returns {Object|null} - Recovered data or null
   */
  recoverData() {
    const mainData = safeLocalStorage.getItem(this.storageKey);
    if (mainData) return mainData;

    const backupData = safeLocalStorage.getItem(this.backupKey);
    if (backupData) {
      console.warn('Recovering data from backup');
      safeLocalStorage.setItem(this.storageKey, backupData);
      return backupData;
    }

    return null;
  }

  /**
   * Clean up old storage keys from previous forms
   * This should be called when starting a new form to prevent accumulation
   * @param {string} currentFormId - The current form identifier (tripId, agentId, etc.)
   */
  static cleanupOldKeys(currentFormId) {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }

      const keysToRemove = [];
      
      // Find all parkpro-form keys that don't match the current form
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('parkpro-form')) {
          // Extract form ID from the key
          let formIdFromKey = null;
          
          if (key.includes('parkpro-form-agent-') && !key.includes('SUBMITTED')) {
            // Agent form: parkpro-form-agent-{agentId}
            formIdFromKey = key.replace('parkpro-form-agent-', '');
          } else if (key.includes('parkpro-form-agency-') && !key.includes('SUBMITTED')) {
            // Agency form: parkpro-form-agency-{agencySlug}-agent-{agentSlug}
            const parts = key.split('-');
            if (parts.length >= 5) {
              formIdFromKey = parts.slice(2).join('-'); // Get everything after 'parkpro-form-agency-'
            }
          } else if (key.startsWith('parkpro-form-') && !key.includes('agent') && !key.includes('SUBMITTED')) {
            // Regular form: parkpro-form-{tripId}
            formIdFromKey = key.replace('parkpro-form-', '');
          }
          
          // If this key belongs to a different form, mark it for removal
          if (formIdFromKey && formIdFromKey !== currentFormId) {
            keysToRemove.push(key);
          }
        }
      }
      
      // Remove old keys
      keysToRemove.forEach(key => {
        console.log(`🧹 Cleaning up old storage key: ${key}`);
        localStorage.removeItem(key);
      });
      
      if (keysToRemove.length > 0) {
        console.log(`🧹 Cleaned up ${keysToRemove.length} old storage keys`);
      }
    } catch (error) {
      console.error('Error cleaning up old storage keys:', error);
    }
  }
}

/**
 * Prevent race conditions with submission locking
 */
export class SubmissionLock {
  constructor() {
    this.activeSubmissions = new Map();
  }

  /**
   * Try to acquire submission lock
   * @param {string} formId - Unique form identifier
   * @returns {boolean} - True if lock acquired
   */
  acquireLock(formId) {
    const now = Date.now();
    const existingLock = this.activeSubmissions.get(formId);
    
    if (existingLock && now - existingLock < 30000) { // 30 second timeout
      return false; // Lock already active
    }
    
    this.activeSubmissions.set(formId, now);
    return true;
  }

  /**
   * Release submission lock
   * @param {string} formId - Unique form identifier
   */
  releaseLock(formId) {
    this.activeSubmissions.delete(formId);
  }

  /**
   * Check if form is currently being submitted
   * @param {string} formId - Unique form identifier
   * @returns {boolean} - True if locked
   */
  isLocked(formId) {
    const lock = this.activeSubmissions.get(formId);
    if (!lock) return false;
    
    const now = Date.now();
    if (now - lock > 30000) { // Clean up expired locks
      this.activeSubmissions.delete(formId);
      return false;
    }
    
    return true;
  }

  /**
   * Clean up expired locks
   */
  cleanup() {
    const now = Date.now();
    for (const [formId, timestamp] of this.activeSubmissions.entries()) {
      if (now - timestamp > 30000) {
        this.activeSubmissions.delete(formId);
      }
    }
  }
}

// Global submission lock instance
export const submissionLock = new SubmissionLock();

// Clean up expired locks every minute
setInterval(() => submissionLock.cleanup(), 60000); 